// src/pages/AdminUsers.js
import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig'; //新增CSRF設定
import API_URL from '../config';
import { useTranslation } from 'react-i18next'; // 引入 i18n
import { useNavigate } from 'react-router-dom'; // 引入 useNavigate
import { useLoading } from '../contexts/LoadingContext';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const { t } = useTranslation(); // 使用 i18n
  const navigate = useNavigate(); // 用於導航
  const { showLoading, hideLoading } = useLoading();

  useEffect(() => {
    const fetchUsers = async () => {
      showLoading();
      try {
        const response = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
        setUsers(response.data);
        setFilteredUsers(response.data);
      } catch (error) {
        console.error('獲取使用者時發生錯誤：', error);
        if (error.response && error.response.status === 401) {
          // 未登入，重定向到登入頁面
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          // 無權限，顯示錯誤訊息或重定向
          alert(t('accessDenied'));
          navigate('/');
        }
      } finally {
        hideLoading();
      }
    };
    
    fetchUsers();
  }, [navigate, t, showLoading, hideLoading]);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = users.filter(user => 
      user.email.toLowerCase().includes(term) || 
      user.name.toLowerCase().includes(term)
    );
    setFilteredUsers(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    const sortedUsers = [...filteredUsers].sort((a, b) => {
      if (key === 'createdAt') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      if (key === 'role') {
        return direction === 'asc'
          ? (a[key] === 'admin' ? 1 : -1)
          : (a[key] === 'admin' ? -1 : 1);
      }
      return direction === 'asc'
        ? a[key].localeCompare(b[key])
        : b[key].localeCompare(a[key]);
    });
    
    setFilteredUsers(sortedUsers);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      setUsers(users.map(user => user._id === userId ? { ...user, role: newRole } : user));
      setFilteredUsers(filteredUsers.map(user => user._id === userId ? { ...user, role: newRole } : user));
    } catch (err) {
      console.error('更新用戶角色時發生錯誤：', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-black dark:text-white">
      {/* 頁面容器 */}
      <div className="container mx-auto px-4 py-6">
        <h2 className="text-2xl font-bold mb-4">{t('userManagement')}</h2>
        
        {/* 搜尋框 */}
        <div className="mb-4">
          <input
            type="text"
            placeholder={t('searchUser')}
            value={searchTerm}
            onChange={handleSearch}
            className="w-full p-2 rounded bg-white dark:bg-gray-800 text-black dark:text-white border dark:border-gray-600"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className="px-4 py-2 border-b dark:border-gray-700 cursor-pointer" onClick={() => handleSort('name')}>
                  {t('name')} {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-2 border-b dark:border-gray-700 cursor-pointer" onClick={() => handleSort('email')}>
                  {t('email')} {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-2 border-b dark:border-gray-700 cursor-pointer" onClick={() => handleSort('role')}>
                  {t('role')} {sortConfig.key === 'role' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-2 border-b dark:border-gray-700 cursor-pointer" onClick={() => handleSort('createdAt')}>
                  {t('registrationDate')} {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-2 border-b dark:border-gray-700">{t('changeRole')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user._id} className="text-center">
                  <td className="px-4 py-2 border-b dark:border-gray-700">{user.name}</td>
                  <td className="px-4 py-2 border-b dark:border-gray-700">{user.email}</td>
                  <td className="px-4 py-2 border-b dark:border-gray-700">{t(user.role)}</td>
                  <td className="px-4 py-2 border-b dark:border-gray-700">
                    {new Date(user.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 border-b dark:border-gray-700">
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user._id, e.target.value)}
                      className="p-2 rounded bg-white dark:bg-gray-800 text-black dark:text-white border dark:border-gray-600"
                    >
                      <option value="user">{t('user')}</option>
                      <option value="admin">{t('admin')}</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminUsers;
